import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useRouter } from '../../hooks/use-router';
import PAGE_ROUTES, { API_ROUTES } from '../../utils/constant';
import { toast } from 'react-toastify';
import HttpService from '../../hooks/Https-services';

interface Invoice {
  id: number;
  invoice_number: number;
  billing_period_start: string;
  billing_period_end: string;
  total: string;
  status: 'DRAFT' | 'SENT';
  generated_at: string;
  client: { company_name: string; customer_id: string };
}

interface Client {
  id: number;
  company_name: string;
}

interface PricingRule {
  id: number;
  description: string;
  team_label: string;
  pricing_type: 'PER_GAME' | 'MONTHLY_FIXED';
  quantity_override: number | null;
}

const InvoiceList: React.FC = () => {
  const router = useRouter();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);
  const [form, setForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    clientId: '',
    invoiceNumber: '',
  });
  const [quantityOverrides, setQuantityOverrides] = useState<Record<number, string>>({});

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await HttpService.get(API_ROUTES.invoiceList);
      setInvoices(res.data.data || []);
    } catch {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await HttpService.get(API_ROUTES.invoiceClients);
      setClients(res.data.data || []);
    } catch {
      toast.error('Failed to load clients');
    }
  };

  const fetchPricingRules = async (clientId: string) => {
    try {
      const res = await HttpService.get(
        `${API_ROUTES.invoicePricing}?clientId=${clientId}`
      );
      const rules: PricingRule[] = res.data.data || [];
      setPricingRules(rules);
      const defaults: Record<number, string> = {};
      rules.forEach((r) => {
        if (r.pricing_type === 'MONTHLY_FIXED') {
          defaults[r.id] = r.quantity_override != null ? String(r.quantity_override) : '';
        }
      });
      setQuantityOverrides(defaults);
    } catch {
      toast.error('Failed to load pricing rules');
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, []);

  const handleClientChange = (clientId: string) => {
    setForm({ ...form, clientId });
    setPricingRules([]);
    setQuantityOverrides({});
    if (clientId) fetchPricingRules(clientId);
  };

  const handleGenerate = async () => {
    if (!form.clientId) {
      toast.error('Select a client');
      return;
    }
    setGenerating(true);
    try {
      const payload: any = {
        year: form.year,
        month: form.month,
        clientId: Number(form.clientId),
        quantityOverrides: Object.fromEntries(
          Object.entries(quantityOverrides).map(([k, v]) => [k, Number(v)])
        ),
      };
      if (form.invoiceNumber) payload.invoiceNumber = Number(form.invoiceNumber);
      await HttpService.post(API_ROUTES.invoiceGenerate, payload);
      toast.success('Invoice generated!');
      setGenerateOpen(false);
      setForm({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, clientId: '', invoiceNumber: '' });
      setPricingRules([]);
      setQuantityOverrides({});
      fetchInvoices();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Invoices
        </Typography>
        <Button
          variant="contained"
          startIcon={<ReceiptIcon />}
          onClick={() => setGenerateOpen(true)}
          sx={{ bgcolor: '#7b6914', '&:hover': { bgcolor: '#5a4d0f' } }}
        >
          Generate Invoice
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#7b6914' }}>
              <TableRow>
                {[
                  'Invoice #',
                  'Client',
                  'Billing Period',
                  'Total',
                  'Status',
                  'Generated At',
                  '',
                ].map((h) => (
                  <TableCell key={h} sx={{ color: '#fff', fontWeight: 'bold' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    router.push(
                      PAGE_ROUTES.INVOICE_DETAIL.replace(':id', String(inv.id))
                    )
                  }
                >
                  <TableCell>{inv.invoice_number}</TableCell>
                  <TableCell>{inv.client?.company_name}</TableCell>
                  <TableCell>
                    {inv.billing_period_start} → {inv.billing_period_end}
                  </TableCell>
                  <TableCell>
                    $
                    {parseFloat(inv.total).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={inv.status}
                      size="small"
                      sx={{
                        bgcolor: inv.status === 'SENT' ? '#d4edda' : '#fff3cd',
                        color: inv.status === 'SENT' ? '#155724' : '#856404',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(inv.generated_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))}
              {!invoices.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No invoices yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Generate Invoice Dialog */}
      <Dialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '16px !important',
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Client</InputLabel>
            <Select
              value={form.clientId}
              label="Client"
              onChange={(e) => handleClientChange(String(e.target.value))}
            >
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Month</InputLabel>
            <Select
              value={form.month}
              label="Month"
              onChange={(e) =>
                setForm({ ...form, month: Number(e.target.value) })
              }
            >
              {months.map((m, i) => (
                <MenuItem key={i} value={i + 1}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Year</InputLabel>
            <Select
              value={form.year}
              label="Year"
              onChange={(e) =>
                setForm({ ...form, year: Number(e.target.value) })
              }
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Invoice Number (optional — leave blank to auto-assign)"
            value={form.invoiceNumber}
            onChange={(e) =>
              setForm({ ...form, invoiceNumber: e.target.value.replace(/\D/g, '') })
            }
            fullWidth
            inputProps={{ inputMode: 'numeric' }}
          />

          {pricingRules.filter((r) => r.pricing_type === 'MONTHLY_FIXED').length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Monthly Fixed Quantities
              </Typography>
              {pricingRules
                .filter((r) => r.pricing_type === 'MONTHLY_FIXED')
                .map((rule) => (
                  <TextField
                    key={rule.id}
                    label={`${rule.description} (${rule.team_label})`}
                    value={quantityOverrides[rule.id] ?? ''}
                    onChange={(e) =>
                      setQuantityOverrides({
                        ...quantityOverrides,
                        [rule.id]: e.target.value.replace(/[^0-9.]/g, ''),
                      })
                    }
                    fullWidth
                    inputProps={{ inputMode: 'decimal' }}
                  />
                ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGenerateOpen(false); setForm({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, clientId: '', invoiceNumber: '' }); setPricingRules([]); setQuantityOverrides({}); }} disabled={generating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating}
            sx={{ bgcolor: '#7b6914', '&:hover': { bgcolor: '#5a4d0f' } }}
          >
            {generating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Generate'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;
